/**
 * Inline Working Hours Editor
 * Click to edit working hours with time inputs in a popover
 */

import React, { useState } from 'react';
import {
  Box,
  Popover,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Grid,
} from '@mui/material';
import { Check, Close, Edit, AccessTime } from '@mui/icons-material';
import toast from 'react-hot-toast';
import driverService from '@/services/driverService';

interface InlineWorkingHoursEditorProps {
  driverId: string;
  currentHours: { start: string; end: string } | null;
  onUpdate: () => void;
}

export function InlineWorkingHoursEditor({
  driverId,
  currentHours,
  onUpdate,
}: InlineWorkingHoursEditorProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [startTime, setStartTime] = useState(currentHours?.start || '08:00');
  const [endTime, setEndTime] = useState(currentHours?.end || '20:00');
  const [saving, setSaving] = useState(false);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setStartTime(currentHours?.start || '08:00');
    setEndTime(currentHours?.end || '20:00');
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSave = async () => {
    if (startTime >= endTime) {
      toast.error('Start time must be before end time');
      return;
    }

    setSaving(true);
    try {

      // Use working_hours to match backend expected field name
      const result = await driverService.updateDriverDetails(driverId, {
        working_hours: { start: startTime, end: endTime },
      });

      toast.success('Working hours updated');
      onUpdate();
      handleClose();
    } catch (error: any) {
      console.error('❌ Failed to update working hours:', error);
      toast.error(error.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          '&:hover': { opacity: 0.7 },
        }}
      >
        {currentHours ? (
          <>
            <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2">
              {currentHours.start} - {currentHours.end}
            </Typography>
            <Edit sx={{ fontSize: 14, color: 'text.secondary' }} />
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTime sx={{ fontSize: 16 }} />
            Click to set
          </Typography>
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 280 }}>
          <Typography variant="subtitle2" gutterBottom>
            Working Hours
          </Typography>
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Start"
                type="time"
                size="small"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="End"
                type="time"
                size="small"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={handleClose} startIcon={<Close />}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={14} /> : <Check />}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
